import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { FileSpreadsheet, Download, Upload, Settings, X, CheckCircle, AlertCircle, Clock } from 'lucide-react';

const FG_CATEGORIES = ['Cubes', 'Notchbar', 'Shots', 'Ingots'];

const STATUS_STYLE = {
  'Valid': 'bg-green-100 text-green-700',
  'Warning': 'bg-amber-100 text-amber-700',
  'Error': 'bg-red-100 text-red-700',
  'Hold for Review': 'bg-blue-100 text-blue-700',
};

export const ExcelImportPanel = ({ selectedDate, selectedShift, onImported }) => {
  const { API } = useContext(AppContext);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showMappings, setShowMappings] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [needConfirm, setNeedConfirm] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const downloadTemplate = () => {
    const url = `${API}/production-import/template?date=${selectedDate}&shift=${selectedShift}`;
    window.open(url, '_blank');
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setError(''); setResult(null); setParsing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/production-import/parse`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse workbook');
    } finally {
      setParsing(false);
      e.target.value = '';
    }
  };

  const s = result?.summary;

  const doCommit = async (confirmReplace) => {
    setCommitting(true); setError(''); setNeedConfirm(null);
    try {
      const res = await axios.post(`${API}/production-import/commit`, {
        date: result.parsedDate, shift: result.parsedShift,
        rows: result.rows, confirmReplace, fileName: result.fileName,
      });
      if (res.data.needsConfirmation) {
        setNeedConfirm(res.data);
      } else {
        setCommitResult(res.data);
        if (onImported) onImported();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="excel-import-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="text-emerald-600" size={20} />
          <h2 className="text-base font-semibold text-gray-900">Excel Import / Export</h2>
          <span className="text-xs text-gray-500">({selectedDate} · {selectedShift})</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadTemplate} data-testid="download-template-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition">
            <Download size={15} /> Download Template
          </button>
          <label data-testid="import-excel-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition cursor-pointer">
            <Upload size={15} /> Import Excel File
            <input type="file" accept=".xlsx" className="hidden" onChange={handleUpload} />
          </label>
          <button onClick={() => setShowMappings(true)} data-testid="open-mappings-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition">
            <Settings size={15} /> Mappings
          </button>
        </div>
      </div>

      {parsing && <p className="mt-3 text-sm text-blue-600">Parsing {fileName}…</p>}
      {error && (
        <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="import-error">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {result && (
        <div className="mt-4" data-testid="import-preview">
          <div className="flex flex-wrap gap-2 text-xs mb-3">
            <Badge label={`Date: ${result.parsedDate || '—'}`} />
            <Badge label={`Shift: ${result.parsedShift || '—'}`} />
            <Badge label={`Furnaces: ${s.furnaces}`} />
            <Badge label={`Valid: ${s.valid}`} tone="green" />
            <Badge label={`Warnings: ${s.warnings}`} tone="amber" />
            <Badge label={`Errors: ${s.errors}`} tone="red" />
            <Badge label={`Hold: ${s.hold}`} tone="blue" />
            {result.existingEntriesForShift > 0 &&
              <Badge label={`Existing entries: ${result.existingEntriesForShift}`} tone="amber" />}
          </div>

          {(s.unmappedMaterials.length > 0 || s.unmappedFG.length > 0) && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800" data-testid="unmapped-warning">
              {s.unmappedMaterials.length > 0 && <div>Unmapped materials: <strong>{s.unmappedMaterials.join(', ')}</strong></div>}
              {s.unmappedFG.length > 0 && <div>Unmapped finished goods: <strong>{s.unmappedFG.join(', ')}</strong></div>}
              <button onClick={() => setShowMappings(true)} className="mt-1 underline">Configure mappings →</button>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Furnace', 'Contractor', 'Operator', 'Materials', 'Total In', 'Total Out', 'Dross', 'B.Loss', 'FG', 'Status'].map(h =>
                    <th key={h} className="px-2 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100" data-testid={`preview-row-${r.furnace}`}>
                    <td className="px-2 py-1.5 font-medium">{r.furnace}</td>
                    <td className="px-2 py-1.5">{r.contractor || '—'}</td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate" title={r.operator}>{r.operator || '—'}</td>
                    <td className="px-2 py-1.5">
                      {r.materials.map((m, j) => (
                        <span key={j} className={`inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded ${m.mapped ? 'bg-gray-100' : 'bg-red-100 text-red-700'}`}>
                          {m.excelLabel}:{m.weight}
                        </span>
                      ))}
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.totalInput}</td>
                    <td className="px-2 py-1.5 text-right">{r.totalOutput ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right">{r.drossUnknown ? <span className="text-blue-600">UNKNOWN</span> : r.dross}</td>
                    <td className="px-2 py-1.5 text-right">{r.burningLoss ?? '—'}</td>
                    <td className="px-2 py-1.5">{r.fgExcel || '—'}{r.fgCategory ? ` → ${r.fgCategory}` : ''}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                      {(r.errors.length > 0 || r.warnings.length > 0) && (
                        <div className="mt-1 text-[10px] text-gray-500" title={[...r.errors, ...r.warnings].join('; ')}>
                          {r.errors[0] || r.warnings[0]}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              disabled={!result.canImport || committing}
              onClick={() => doCommit(false)}
              data-testid="import-confirm-btn"
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg ${
                result.canImport && !committing
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
              <Upload size={15} /> {committing ? 'Importing…' : 'Import & Save'}
            </button>
            {result.canImport
              ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={14} /> No blocking errors</span>
              : <span className="text-xs text-red-600 flex items-center gap-1"><AlertCircle size={14} /> Resolve errors before import</span>}
            <button onClick={() => setShowHistory(true)} data-testid="view-history-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Clock size={15} /> Import History
            </button>
          </div>

          {commitResult && (
            <div className={`mt-3 rounded-lg p-3 text-sm border ${
              commitResult.status === 'Imported'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="commit-result">
              <div className="font-semibold">{commitResult.status}: {commitResult.message}</div>
              <div className="mt-1 text-xs">
                Imported: {commitResult.imported} · Verified: {(commitResult.verifiedFurnaces || []).join(', ') || '—'}
                {commitResult.failedFurnaces?.length > 0 && <> · Failed: {commitResult.failedFurnaces.join(', ')}</>}
                {commitResult.held?.length > 0 && <> · Held: {commitResult.held.map(h => h.furnace).join(', ')}</>}
              </div>
              {commitResult.stockReconciliation && (
                <div className="mt-1 text-xs">
                  Stock reconciliation: {commitResult.stockReconciliation.ok ? 'OK ✓' : 'MISMATCH ✗ (manual review)'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {needConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="replace-confirm-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5">
            <h3 className="font-semibold text-gray-900">Records already exist</h3>
            <p className="mt-2 text-sm text-gray-600">
              {result.parsedDate} · {result.parsedShift} already has entries for furnaces:
              <strong> {needConfirm.overlap.join(', ')}</strong>. Importing will overwrite these records.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setNeedConfirm(null)} className="px-3 py-2 text-sm rounded-lg border border-gray-300">Cancel</button>
              <button onClick={() => doCommit(true)} data-testid="confirm-replace-btn"
                className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700">Replace &amp; Import</button>
            </div>
          </div>
        </div>
      )}

      {showHistory && <HistoryModal API={API} onClose={() => setShowHistory(false)} />}

      {showMappings && <MappingsModal API={API} onClose={() => setShowMappings(false)} />}
    </div>
  );
};

const Badge = ({ label, tone }) => {
  const tones = { green: 'bg-green-100 text-green-700', amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700', blue: 'bg-blue-100 text-blue-700' };
  return <span className={`px-2 py-1 rounded ${tones[tone] || 'bg-gray-100 text-gray-700'}`}>{label}</span>;
};

const MappingsModal = ({ API, onClose }) => {
  const [tab, setTab] = useState('material');
  const [matMap, setMatMap] = useState([]);
  const [fgMap, setFgMap] = useState([]);
  const [erpTypes, setErpTypes] = useState([]);
  const [mForm, setMForm] = useState({ excelLabel: '', erpScrapType: '' });
  const [fForm, setFForm] = useState({ excelValue: '', erpCategory: 'Cubes' });

  const load = async () => {
    const [m, f, t] = await Promise.all([
      axios.get(`${API}/production-import/material-map`),
      axios.get(`${API}/production-import/fg-map`),
      axios.get(`${API}/production-import/erp-scrap-types`),
    ]);
    setMatMap(m.data); setFgMap(f.data); setErpTypes(t.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveMat = async () => {
    if (!mForm.excelLabel || !mForm.erpScrapType) return;
    await axios.post(`${API}/production-import/material-map`, { ...mForm, approved: true });
    setMForm({ excelLabel: '', erpScrapType: '' }); load();
  };
  const delMat = async (l) => { await axios.delete(`${API}/production-import/material-map/${encodeURIComponent(l)}`); load(); };
  const saveFg = async () => {
    if (!fForm.excelValue) return;
    await axios.post(`${API}/production-import/fg-map`, { ...fForm, approved: true });
    setFForm({ excelValue: '', erpCategory: 'Cubes' }); load();
  };
  const delFg = async (v) => { await axios.delete(`${API}/production-import/fg-map/${encodeURIComponent(v)}`); load(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mappings-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Import Mappings</h3>
          <button onClick={onClose} data-testid="close-mappings-btn"><X size={18} /></button>
        </div>
        <div className="flex gap-1 px-4 pt-3">
          {['material', 'fg'].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-3 py-1.5 text-sm rounded-t-lg ${tab === t ? 'bg-gray-100 font-medium' : 'text-gray-500'}`}>
              {t === 'material' ? 'Materials' : 'Finished Goods'}
            </button>
          ))}
        </div>
        <div className="p-4 overflow-y-auto">
          {tab === 'material' ? (
            <>
              <div className="flex gap-2 mb-3">
                <input placeholder="Excel label (exact)" value={mForm.excelLabel}
                  onChange={e => setMForm({ ...mForm, excelLabel: e.target.value })}
                  data-testid="mat-excel-input"
                  className="flex-1 px-2 py-1.5 border rounded text-sm" />
                <select value={mForm.erpScrapType} onChange={e => setMForm({ ...mForm, erpScrapType: e.target.value })}
                  data-testid="mat-erp-select" className="flex-1 px-2 py-1.5 border rounded text-sm">
                  <option value="">Select ERP material…</option>
                  {erpTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={saveMat} data-testid="mat-save-btn" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Add</button>
              </div>
              <table className="min-w-full text-sm">
                <thead className="text-gray-500 text-xs"><tr><th className="text-left py-1">Excel Label</th><th className="text-left">ERP Material</th><th></th></tr></thead>
                <tbody>
                  {matMap.map(m => (
                    <tr key={m.excelLabel} className="border-t">
                      <td className="py-1.5 font-mono text-xs">"{m.excelLabel}"</td>
                      <td>{m.erpScrapType}</td>
                      <td className="text-right"><button onClick={() => delMat(m.excelLabel)} className="text-red-500 text-xs">Remove</button></td>
                    </tr>
                  ))}
                  {matMap.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-gray-400 text-xs">No mappings yet</td></tr>}
                </tbody>
              </table>
            </>
          ) : (
            <>
              <div className="flex gap-2 mb-3">
                <input placeholder="Excel FG value (e.g. NB)" value={fForm.excelValue}
                  onChange={e => setFForm({ ...fForm, excelValue: e.target.value })}
                  data-testid="fg-excel-input" className="flex-1 px-2 py-1.5 border rounded text-sm" />
                <select value={fForm.erpCategory} onChange={e => setFForm({ ...fForm, erpCategory: e.target.value })}
                  data-testid="fg-erp-select" className="flex-1 px-2 py-1.5 border rounded text-sm">
                  {FG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={saveFg} data-testid="fg-save-btn" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Add</button>
              </div>
              <table className="min-w-full text-sm">
                <thead className="text-gray-500 text-xs"><tr><th className="text-left py-1">Excel Value</th><th className="text-left">ERP Category</th><th></th></tr></thead>
                <tbody>
                  {fgMap.map(m => (
                    <tr key={m.excelValue} className="border-t">
                      <td className="py-1.5 font-mono text-xs">"{m.excelValue}"</td>
                      <td>{m.erpCategory}</td>
                      <td className="text-right"><button onClick={() => delFg(m.excelValue)} className="text-red-500 text-xs">Remove</button></td>
                    </tr>
                  ))}
                  {fgMap.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-gray-400 text-xs">No mappings yet</td></tr>}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportPanel;

const HistoryModal = ({ API, onClose }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/production-import/history?limit=25`).then(r => setRows(r.data)).catch(() => {});
  }, []); // eslint-disable-line
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="history-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Import History</h3>
          <button onClick={onClose} data-testid="close-history-btn"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          <table className="min-w-full text-xs">
            <thead className="text-gray-500"><tr>
              {['When', 'File', 'Date', 'Shift', 'Detected', 'Imported', 'Held', 'Status'].map(h =>
                <th key={h} className="text-left py-1 px-2">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1.5 px-2 whitespace-nowrap">{(r.uploadedAt || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-2 max-w-[160px] truncate" title={r.fileName}>{r.fileName || '—'}</td>
                  <td className="px-2">{r.date}</td>
                  <td className="px-2">{r.shift}</td>
                  <td className="px-2">{r.furnacesDetected}</td>
                  <td className="px-2">{r.imported}</td>
                  <td className="px-2">{r.held}</td>
                  <td className="px-2">
                    <span className={`px-2 py-0.5 rounded-full ${r.status === 'Imported' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-gray-400">No imports yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
